import * as React from 'react';
import { FormattedMessage, MessageDescriptor, useIntl } from 'react-intl';

import { TooltipModel } from '@notino/react-styleguide';
import { GiftType } from '@notino/shared/definitions/types';

import { useDrawer } from '@components/BottomSheet/DrawerProvider';
import { PortalTooltip } from '@components/PortalTooltip';

import messages from '../../../../messages';
import { LoyaltySymbol } from '../../LoyaltyProgramBanner/LoyaltySymbol';
import { GiftImage } from '../GiftImage/GiftImage';
import { GiftImageThumb } from '../GiftImage/GiftImageThumb';
import { GiftImageBlockWrapper } from '../GiftImage/styled';

import {
  Description,
  TooltipWrapper,
  GiftBaseItemWrapper,
  GiftContent,
  LoyaltyLabel,
} from './styled';

interface GiftBaseItemProps {
  imageUrl: string;
  descriptionText: string;
  imageMessage: MessageDescriptor;
  giftType?: GiftType | null;
}

export const GiftBaseItem: React.FC<GiftBaseItemProps> = ({
  descriptionText,
  imageUrl,
  imageMessage,
  giftType,
}) => {
  const { formatMessage } = useIntl();

  const { toggleDrawer } = useDrawer();

  const isLoyaltyGift = giftType === GiftType.Loyalty;

  const giftImage = React.useMemo(
    () =>
      imageUrl ? (
        <GiftImage
          formattedImageMessage={formatMessage(imageMessage)}
          imageUrl={imageUrl}
        />
      ) : null,
    [imageUrl, formatMessage, imageMessage]
  );

  const omOpenDrawer = () => {
    toggleDrawer(giftImage, { header: formatMessage(imageMessage) });
  };

  return (
    <GiftBaseItemWrapper data-testid="gift-base-item">
      <TooltipWrapper>
        <PortalTooltip
          content={<PortalTooltip.Content>{giftImage}</PortalTooltip.Content>}
          position={TooltipModel.Position.topLeft}
          disabled={!imageUrl}
        >
          <GiftImageThumb
            sourceKey="thumb"
            imageUrl={imageUrl}
            formattedImageMessage={formatMessage(imageMessage)}
          />
        </PortalTooltip>
      </TooltipWrapper>

      <GiftImageBlockWrapper>
        <GiftImageThumb
          sourceKey="list_2k"
          imageUrl={imageUrl}
          formattedImageMessage={formatMessage(imageMessage)}
          onClick={omOpenDrawer}
        />
      </GiftImageBlockWrapper>

      <GiftContent>
        {isLoyaltyGift && (
          <LoyaltyLabel data-testid="gift-loyalty-label">
            <LoyaltySymbol />
            <FormattedMessage {...messages.giftLoyaltyLabel} />
          </LoyaltyLabel>
        )}

        <Description dangerouslySetInnerHTML={{ __html: descriptionText }} />
      </GiftContent>
    </GiftBaseItemWrapper>
  );
};
